import React, { useState, useRef, useEffect, useMemo, Fragment } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { arrayOf, object, func, string, bool, shape } from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { useTheme, useMediaQuery, Typography, Divider, Box, styled } from '@mui/material'
import Stack from '@mui/material/Stack'
import { letters } from '@helpers/string-helper'
import { useMyStore } from '@hooks/useMyStore'
import useStoreLocatorContext from '@context/storeLocatorContext/useStoreLocatorContext'

import '../../assets/css/components/store-locator/map-locations-list.sass'
import InStoreScheduleModal from '@shared/modals/InStoreSchedule'
// import store from '@redux/store'
import WarningMessage from './warning-message'

import StoreListDetail from '../StoreListDetail/StoreListDetail'
import { getSpecialClosings } from './helpers'
import MapLocationMyStore from './map-locations-mystore'

const LocationsContainer = styled(Stack, {
  shouldForwardProp: prop => prop !== 'isMobile',
})(({ isMobile }) => ({
  display: 'flex',
  flexDirection: 'column',
  contentVisibility: 'auto',
  marginLeft: '-15px',
  marginRight: '-15px',
  ...(!isMobile && {
    overflow: 'auto',
    height: 'auto',
  }),
  padding: 0,
  ...(isMobile && {}),
}))

export const scrollToSelectedMarker = refItem =>
  typeof refItem !== 'undefined'
    ? refItem?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
    : null

export const StoreWrapper = styled('div')(({ theme, selected }) => ({
  cursor: 'pointer',
  margin: 16,
  marginBottom: 0,
  '& > div': {
    ...(selected && { borderColor: theme.palette.primary.main }),
  },
}))

export const StyledStoreContainer = styled('div')(({ theme, isMyPreferredStore, productInStore }) => ({
  ...(isMyPreferredStore && !!productInStore
    ? {
        backgroundColor: theme.palette.common.white,
        marginBottom: '21px',
        div: {
          '& > .MuiPaper-root': {
            borderColor: `1.5px solid ${theme.palette.primary.main}`,
          },
        },
      }
    : { backgroundColor: '#f6f8fb' }),
}))

const MapLocationList = ({ defaultStores, showRatings, isMyStore, productInStore, renderSearchBar }) => {
  const stores = useStoreLocatorContext(state => state.stores)
  const selectStore = useStoreLocatorContext(state => state.selectStore)
  const selectedStore = useStoreLocatorContext(state => state.selectedStore)
  const myStoreInContext = useStoreLocatorContext(state => state.myStore)
  const markers = useStoreLocatorContext(state => state.markers)
  const refSelectedMarker = useRef()
  const data = useStaticQuery(query)
  const [scheduleModalData, setScheduleModalData] = useState({ show: false, store: null })
  const { setMyStoreData } = useMyStore()

  const handleShowroomClick = store => {
    setScheduleModalData({ show: true, store })
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    if (selectedStore && refSelectedMarker.current) {
      scrollToSelectedMarker(refSelectedMarker)
    }
  }, [selectedStore])

  const closeModal = () => setScheduleModalData({ show: false, store: null })

  const setMyStore = storeNumber => {
    setMyStoreData(defaultStores.find(store => store.StoreNumber === storeNumber))
  }

  const myStoreMarker = useMemo(() => stores?.find(store => store.storeNumber === myStoreInContext?.storeNumber), [
    stores,
    myStoreInContext,
  ])

  const myStoreSpecialClosings = getSpecialClosings({ data, mark: myStoreMarker })

  return (
    <LocationsContainer isMobile={isMobile} padding={2}>
      <MapLocationMyStore showRatings specialClosings={myStoreSpecialClosings} />
      {stores?.map((mark, index) => {
        if (mark.storeNumber === myStoreInContext?.storeNumber) return null
        const storeDetails = {
          city: mark?.city || mark?.storeCity,
          distance: mark?.distance,
          hours: mark?.hours || mark?.storeRegularHours,
          name: mark?.name || mark?.storeName,
          phone:
            mark?.phone?.replace('/', '-') ||
            mark?.phoneNumber?.replace('/', '-') ||
            mark?.storePhoneNumber?.replace('/', '-'),
          selectedRatings: mark?.ratings?.lifetime || {},
          state: mark?.state || mark?.storeState,
          storeNumber: mark.storeNumber ? `${mark.storeNumber}` : null,
          title: mark?.title || mark?.storeTitle || mark?.address1,
          types: mark?.types || [],
          zip: mark?.zip || mark?.storeZip,
        }

        const specialClosings = getSpecialClosings({ data, mark })
        const markerIsSelected = !isEmpty(selectedStore) && selectedStore?.storeNumber === mark?.storeNumber
        const isMyPreferredStore = +myStoreInContext?.storeNumber === +storeDetails?.storeNumber

        return (
          <Box
            key={`${mark.title}|${mark.name}`}
            style={{ display: 'flex', flexDirection: isMyPreferredStore ? 'column' : 'column-reverse' }}
          >
            <StyledStoreContainer isMyPreferredStore={isMyPreferredStore} productInStore={productInStore}>
              <StoreWrapper
                {...(markerIsSelected && { ref: refSelectedMarker })}
                selected={markerIsSelected || isMyPreferredStore}
                role="button"
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex="0"
                onClick={() => selectStore(mark.StoreNumber)}
                onKeyDown={e => {
                  if (e.keyCode === 13) selectStore(mark.StoreNumber)
                }}
              >
                <StoreListDetail
                  handleShowroomClick={() => handleShowroomClick(mark)}
                  isSelected={markerIsSelected}
                  // letter={letters[index]}
                  letter={markers?.[mark.storeNumber]?.letter}
                  specialClosings={specialClosings}
                  showRatings={!!showRatings}
                  storeDetails={storeDetails}
                  myStore={isMyStore}
                  isDefaultUserStore={isMyPreferredStore}
                  onSetMyStore={() => setMyStore(mark.storeNumber)}
                  noStoreNameLink
                />
              </StoreWrapper>
            </StyledStoreContainer>

            <StyledStoreContainer>
              {index === 0 && (
                <>
                  {!!productInStore?.sku && <Divider />}
                  {stores?.length > 0 && isMyStore && !!productInStore?.sku && (
                    <Box style={{ padding: '1rem' }}>{renderSearchBar()}</Box>
                  )}
                  {stores?.length === 0 && <WarningMessage isMobile={isMobile} />}
                </>
              )}
            </StyledStoreContainer>
          </Box>
        )
      })}
      {stores?.length === 0 && (
        <>
          <StyledStoreContainer>
            {isMyStore && !!productInStore?.sku && (
              <>
                <Divider />
                <Box style={{ padding: '1rem' }}>{renderSearchBar()}</Box>
              </>
            )}
          </StyledStoreContainer>
          {(!productInStore || (productInStore && !productInStore?.availability)) && (
            <WarningMessage isMobile={isMobile} />
          )}
        </>
      )}
      {scheduleModalData.store && scheduleModalData.show && (
        <InStoreScheduleModal
          shouldShowModal={scheduleModalData.show}
          store={{
            ...scheduleModalData.store,
            storeNumber: scheduleModalData.store.StoreNumber || scheduleModalData.store.storeNumber,
            StoreNumber: scheduleModalData.store.StoreNumber || scheduleModalData.store.storeNumber,
          }}
          closeModal={closeModal}
          changeLocation={closeModal}
        />
      )}
    </LocationsContainer>
  )
}

MapLocationList.propTypes = {
  groupedStores: arrayOf(object),
  defaultStores: object,
  onLocationClick: func,
  lia: string,
  selectedMarker: object,
  showRatings: bool,
  isMyStore: bool,
  productInStore: bool,
  myStore: object,
  renderSearchBar: func,
}

const query = graphql`
  query StoresClosingsQueryV2 {
    allStrapiStore(filter: { strapi_id: { ne: 0 } }) {
      edges {
        node {
          id
          InStoreAppointments
          StoreNumber
          SpecialClosings {
            Markdown {
              Markdown {
                data {
                  childMarkdownRemark {
                    html
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export default MapLocationList
